import { Link } from 'react-router'

const features = [
  {
    name: 'Daily Updates',
    description:
      'Share what children are doing throughout the day. From activities and meals to nap times and milestones - keep parents in the loop with quick, easy updates.',
    icon: (
      <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
      </svg>
    ),
    color: 'primary',
  },
  {
    name: 'Attendance Tracking',
    description:
      'Check children in when they arrive and out when they leave. Arrival and departure times automatically appear on each child\'s timeline so parents know exactly when their child was in your care.',
    icon: (
      <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
      </svg>
    ),
    color: 'green',
  },
  {
    name: 'Shared Calendar',
    description:
      'Parents can report holidays and sick days directly in the app. Childminders can add activities, reminders, and closures. Everyone stays on the same page with a shared calendar view.',
    icon: (
      <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
      </svg>
    ),
    color: 'blue',
  },
  {
    name: 'Attendance Schedules',
    description:
      'Set which days each child usually attends. The attendance list automatically shows only the children expected that day, making check-in quick and simple.',
    icon: (
      <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
      </svg>
    ),
    color: 'yellow',
  },
  {
    name: 'Photo Sharing',
    description:
      'Capture special moments and share them instantly. Parents can see photos of their children playing, learning, and having fun - right from their phone.',
    icon: (
      <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
      </svg>
    ),
    color: 'accent',
  },
  {
    name: 'Push Notifications',
    description:
      'Parents never miss an update. Instant notifications alert them the moment something new is shared about their child, including when they\'re checked in or out.',
    icon: (
      <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9" />
      </svg>
    ),
    color: 'purple',
  },
  {
    name: 'Secure Messaging',
    description:
      'Direct, encrypted chat between childminders and parents. Childminders can message individual parents, create group chats, or broadcast announcements to everyone.',
    icon: (
      <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
      </svg>
    ),
    color: 'purple',
  },
  {
    name: 'Multiple Children',
    description:
      'Manage all the children in your care from one place. Create separate profiles and easily select which children each update is about.',
    icon: (
      <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
      </svg>
    ),
    color: 'pink',
  },
  {
    name: 'Parent Invitations',
    description:
      'Connecting with parents is simple. Send an email invitation and parents can accept with just a click. No complicated setup required.',
    icon: (
      <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
      </svg>
    ),
    color: 'indigo',
  },
  {
    name: 'App Lock & Security',
    description:
      'Protect the app with PIN or biometric authentication (Face ID, fingerprint). Keep children\'s information safe and secure.',
    icon: (
      <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
      </svg>
    ),
    color: 'gray',
  },
  {
    name: 'Works Everywhere',
    description:
      'Available on iOS, Android, and web. Access Brightseed from any device, anywhere. Your data syncs seamlessly across all platforms.',
    icon: (
      <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 18h.01M8 21h8a2 2 0 002-2V5a2 2 0 00-2-2H8a2 2 0 00-2 2v14a2 2 0 002 2z" />
      </svg>
    ),
    color: 'primary',
  },
]

const colorClasses: Record<string, { bg: string; text: string }> = {
  primary: { bg: 'bg-primary-100', text: 'text-primary-600' },
  accent: { bg: 'bg-accent-100', text: 'text-accent-600' },
  blue: { bg: 'bg-blue-100', text: 'text-blue-600' },
  purple: { bg: 'bg-purple-100', text: 'text-purple-600' },
  pink: { bg: 'bg-pink-100', text: 'text-pink-600' },
  gray: { bg: 'bg-gray-100', text: 'text-gray-600' },
  green: { bg: 'bg-green-100', text: 'text-green-600' },
  indigo: { bg: 'bg-indigo-100', text: 'text-indigo-600' },
  yellow: { bg: 'bg-yellow-100', text: 'text-yellow-600' },
}

export function FeaturesPage() {
  return (
    <div>
      {/* Header */}
      <section className="bg-gradient-to-b from-primary-50 to-white py-20">
        <div className="mx-auto max-w-6xl px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            <h1 className="text-4xl font-bold tracking-tight text-gray-900 sm:text-5xl">
              Everything you need to keep
              <span className="block text-primary-600">parents informed</span>
            </h1>
            <p className="mx-auto mt-6 max-w-2xl text-lg text-gray-600">
              Brightseed is packed with features designed to make sharing updates
              simple for childminders and delightful for parents.
            </p>
          </div>
        </div>
      </section>

      {/* Features Grid */}
      <section className="py-20 bg-white">
        <div className="mx-auto max-w-6xl px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-2 gap-8">
            {features.map((feature) => (
              <div
                key={feature.name}
                className="relative rounded-2xl border border-gray-100 p-8 hover:border-primary-200 hover:shadow-lg transition-all"
              >
                <div
                  className={`w-12 h-12 rounded-xl ${colorClasses[feature.color].bg} ${colorClasses[feature.color].text} flex items-center justify-center mb-4`}
                >
                  {feature.icon}
                </div>
                <h3 className="text-xl font-semibold text-gray-900 mb-3">
                  {feature.name}
                </h3>
                <p className="text-gray-600">{feature.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* For Childminders Section */}
      <section className="py-20 bg-gray-50">
        <div className="mx-auto max-w-6xl px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-2 gap-12 items-center">
            <div>
              <span className="inline-block px-4 py-1 rounded-full bg-primary-100 text-primary-700 text-sm font-medium mb-4">
                For Childminders
              </span>
              <h2 className="text-3xl font-bold text-gray-900 mb-6">
                Save time, delight parents
              </h2>
              <ul className="space-y-4">
                <li className="flex items-start gap-3">
                  <svg className="w-6 h-6 text-primary-600 flex-shrink-0 mt-0.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                  </svg>
                  <span className="text-gray-600">Create updates in seconds, not minutes</span>
                </li>
                <li className="flex items-start gap-3">
                  <svg className="w-6 h-6 text-primary-600 flex-shrink-0 mt-0.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                  </svg>
                  <span className="text-gray-600">Tag multiple children in one update</span>
                </li>
                <li className="flex items-start gap-3">
                  <svg className="w-6 h-6 text-primary-600 flex-shrink-0 mt-0.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                  </svg>
                  <span className="text-gray-600">Manage all children from one dashboard</span>
                </li>
                <li className="flex items-start gap-3">
                  <svg className="w-6 h-6 text-primary-600 flex-shrink-0 mt-0.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                  </svg>
                  <span className="text-gray-600">Professional image for your childminding business</span>
                </li>
                <li className="flex items-start gap-3">
                  <svg className="w-6 h-6 text-primary-600 flex-shrink-0 mt-0.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                  </svg>
                  <span className="text-gray-600">Message parents directly or broadcast to everyone</span>
                </li>
              </ul>
            </div>
            <div className="rounded-2xl overflow-hidden">
              <svg viewBox="0 0 400 400" className="w-full h-auto" xmlns="http://www.w3.org/2000/svg">
                {/* Background */}
                <rect width="400" height="400" rx="16" fill="#dcfce7" />

                {/* Window/light effect */}
                <rect x="280" y="20" width="100" height="100" rx="8" fill="#fef3c7" opacity="0.6" />
                <rect x="290" y="30" width="35" height="40" fill="#fde68a" />
                <rect x="335" y="30" width="35" height="40" fill="#fde68a" />

                {/* Desk */}
                <rect x="30" y="280" width="340" height="16" rx="4" fill="#92400e" />
                <rect x="45" y="296" width="16" height="80" fill="#78350f" />
                <rect x="339" y="296" width="16" height="80" fill="#78350f" />

                {/* Tablet on desk */}
                <g transform="translate(100, 120)">
                  <rect x="0" y="0" width="200" height="150" rx="12" fill="#1f2937" />
                  <rect x="6" y="6" width="188" height="138" rx="8" fill="#f8fafc" />

                  {/* App UI on tablet */}
                  <rect x="14" y="14" width="80" height="20" rx="4" fill="#22c55e" />
                  <text x="24" y="28" fontSize="10" fill="white" fontWeight="bold" fontFamily="system-ui">Dashboard</text>

                  {/* Child cards grid */}
                  <rect x="14" y="42" width="82" height="45" rx="6" fill="white" filter="drop-shadow(0 1px 2px rgba(0,0,0,0.1))" />
                  <circle cx="35" cy="58" r="12" fill="#fef3c7" />
                  <text x="32" y="62" fontSize="10" fill="#f59e0b">★</text>
                  <rect x="52" y="50" width="38" height="5" rx="2" fill="#e2e8f0" />
                  <rect x="52" y="60" width="28" height="4" rx="2" fill="#f1f5f9" />
                  <rect x="14" y="75" width="20" height="8" rx="2" fill="#22c55e" />

                  <rect x="104" y="42" width="82" height="45" rx="6" fill="white" filter="drop-shadow(0 1px 2px rgba(0,0,0,0.1))" />
                  <circle cx="125" cy="58" r="12" fill="#dbeafe" />
                  <text x="122" y="62" fontSize="10" fill="#3b82f6">★</text>
                  <rect x="142" y="50" width="38" height="5" rx="2" fill="#e2e8f0" />
                  <rect x="142" y="60" width="28" height="4" rx="2" fill="#f1f5f9" />
                  <rect x="104" y="75" width="20" height="8" rx="2" fill="#22c55e" />

                  {/* Add update button */}
                  <rect x="14" y="100" width="172" height="35" rx="8" fill="#22c55e" />
                  <text x="60" y="122" fontSize="12" fill="white" fontWeight="bold" fontFamily="system-ui">+ Add Update</text>
                </g>

                {/* Camera */}
                <g transform="translate(40, 180)">
                  <rect x="0" y="0" width="50" height="38" rx="6" fill="#6b7280" />
                  <circle cx="25" cy="20" r="12" fill="#1f2937" />
                  <circle cx="25" cy="20" r="7" fill="#3b82f6" />
                  <circle cx="25" cy="20" r="3" fill="#60a5fa" />
                  <rect x="35" y="5" width="10" height="6" rx="2" fill="#9ca3af" />
                </g>

                {/* Pencil cup */}
                <g transform="translate(320, 220)">
                  <rect x="0" y="20" width="40" height="50" rx="4" fill="#f472b6" />
                  <rect x="5" y="25" width="30" height="8" rx="2" fill="#f9a8d4" opacity="0.5" />
                  <rect x="8" y="0" width="6" height="30" fill="#fbbf24" />
                  <rect x="18" y="5" width="6" height="25" fill="#22c55e" />
                  <rect x="28" y="2" width="6" height="28" fill="#3b82f6" />
                </g>

                {/* Coffee cup */}
                <g transform="translate(50, 230)">
                  <ellipse cx="20" cy="45" rx="18" ry="6" fill="#78350f" />
                  <rect x="2" y="8" width="36" height="37" rx="4" fill="#f5f5f4" />
                  <ellipse cx="20" cy="10" rx="16" ry="5" fill="#92400e" />
                  <path d="M38 18 Q50 22 38 32" stroke="#f5f5f4" strokeWidth="5" fill="none" />
                  {/* Steam */}
                  <path d="M15 0 Q12 -8 15 -12" stroke="#d1d5db" strokeWidth="2" fill="none" opacity="0.6" />
                  <path d="M25 -2 Q28 -10 25 -15" stroke="#d1d5db" strokeWidth="2" fill="none" opacity="0.6" />
                </g>

                {/* Floating notification cards */}
                <g transform="translate(20, 50)">
                  <rect x="0" y="0" width="80" height="45" rx="8" fill="white" filter="drop-shadow(0 2px 4px rgba(0,0,0,0.1))" />
                  <circle cx="20" cy="22" r="12" fill="#dcfce7" />
                  <path d="M16 22 L19 25 L26 18" stroke="#22c55e" strokeWidth="2" fill="none" strokeLinecap="round" />
                  <rect x="38" y="14" width="35" height="5" rx="2" fill="#e2e8f0" />
                  <rect x="38" y="24" width="25" height="4" rx="2" fill="#f1f5f9" />
                </g>

                <g transform="translate(280, 140)">
                  <rect x="0" y="0" width="80" height="45" rx="8" fill="white" filter="drop-shadow(0 2px 4px rgba(0,0,0,0.1))" />
                  <circle cx="20" cy="22" r="12" fill="#fef3c7" />
                  <text x="16" y="27" fontSize="14" fill="#f59e0b">📷</text>
                  <rect x="38" y="14" width="35" height="5" rx="2" fill="#e2e8f0" />
                  <rect x="38" y="24" width="25" height="4" rx="2" fill="#f1f5f9" />
                </g>

                {/* Sparkles */}
                <g fill="#fbbf24">
                  <polygon points="350,50 352,56 358,56 354,60 355,66 350,62 345,66 346,60 342,56 348,56" transform="scale(0.6)" />
                  <polygon points="120,320 122,326 128,326 124,330 125,336 120,332 115,336 116,330 112,326 118,326" transform="scale(0.5)" />
                </g>

                {/* Check mark badge */}
                <circle cx="330" cy="100" r="18" fill="#22c55e" />
                <path d="M322 100 L328 106 L340 94" stroke="white" strokeWidth="3" fill="none" strokeLinecap="round" strokeLinejoin="round" />
              </svg>
            </div>
          </div>
        </div>
      </section>

      {/* For Parents Section */}
      <section className="py-20 bg-white">
        <div className="mx-auto max-w-6xl px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-2 gap-12 items-center">
            <div className="order-2 md:order-1 rounded-2xl overflow-hidden">
              <svg viewBox="0 0 400 400" className="w-full h-auto" xmlns="http://www.w3.org/2000/svg">
                {/* Background - cozy home setting */}
                <rect width="400" height="400" rx="16" fill="#fef3c7" />

                {/* Window */}
                <rect x="20" y="30" width="100" height="120" rx="8" fill="#bfdbfe" />
                <line x1="70" y1="30" x2="70" y2="150" stroke="white" strokeWidth="3" />
                <line x1="20" y1="90" x2="120" y2="90" stroke="white" strokeWidth="3" />
                {/* Sun through window */}
                <circle cx="55" cy="60" r="18" fill="#fbbf24" />
                <g stroke="#fbbf24" strokeWidth="2" strokeLinecap="round">
                  <line x1="55" y1="38" x2="55" y2="32" />
                  <line x1="55" y1="88" x2="55" y2="82" />
                  <line x1="33" y1="60" x2="27" y2="60" />
                  <line x1="83" y1="60" x2="77" y2="60" />
                </g>

                {/* Couch/sofa */}
                <rect x="140" y="280" width="220" height="80" rx="12" fill="#8b5cf6" />
                <rect x="150" y="270" width="200" height="20" rx="8" fill="#a78bfa" />
                <rect x="140" y="260" width="30" height="100" rx="8" fill="#7c3aed" />
                <rect x="330" y="260" width="30" height="100" rx="8" fill="#7c3aed" />

                {/* Phone on couch - main focus */}
                <g transform="translate(200, 140)">
                  <rect x="0" y="0" width="100" height="170" rx="12" fill="#1f2937" />
                  <rect x="5" y="8" width="90" height="154" rx="8" fill="#f8fafc" />

                  {/* Status bar */}
                  <rect x="12" y="16" width="30" height="4" rx="2" fill="#e2e8f0" />
                  <rect x="58" y="16" width="30" height="4" rx="2" fill="#e2e8f0" />

                  {/* Notification banner */}
                  <rect x="10" y="28" width="80" height="35" rx="6" fill="#22c55e" />
                  <circle cx="28" cy="45" r="10" fill="white" opacity="0.3" />
                  <text x="42" y="42" fontSize="8" fill="white" fontWeight="bold" fontFamily="system-ui">New Update!</text>
                  <text x="42" y="52" fontSize="6" fill="white" opacity="0.9" fontFamily="system-ui">Just now</text>

                  {/* Update cards */}
                  <rect x="10" y="70" width="80" height="45" rx="6" fill="white" filter="drop-shadow(0 1px 2px rgba(0,0,0,0.1))" />
                  <rect x="16" y="76" width="28" height="28" rx="4" fill="#dcfce7" />
                  <path d="M25 88 L29 92 L37 84" stroke="#22c55e" strokeWidth="2" fill="none" strokeLinecap="round" />
                  <rect x="50" y="78" width="35" height="5" rx="2" fill="#22c55e" />
                  <rect x="50" y="88" width="28" height="4" rx="2" fill="#e2e8f0" />
                  <rect x="50" y="96" width="20" height="4" rx="2" fill="#f1f5f9" />

                  <rect x="10" y="120" width="80" height="38" rx="6" fill="white" filter="drop-shadow(0 1px 2px rgba(0,0,0,0.1))" />
                  <rect x="16" y="126" width="22" height="22" rx="4" fill="#fef3c7" />
                  <text x="23" y="141" fontSize="12" fill="#f59e0b">📷</text>
                  <rect x="44" y="128" width="40" height="5" rx="2" fill="#f97316" />
                  <rect x="44" y="138" width="32" height="4" rx="2" fill="#e2e8f0" />
                  <rect x="44" y="146" width="24" height="4" rx="2" fill="#f1f5f9" />

                  {/* Notification badge */}
                  <circle cx="92" cy="12" r="10" fill="#ef4444" />
                  <text x="92" y="16" fontSize="10" fill="white" textAnchor="middle" fontWeight="bold">3</text>
                </g>

                {/* Coffee cup */}
                <g transform="translate(320, 300)">
                  <ellipse cx="20" cy="40" rx="18" ry="6" fill="#78350f" />
                  <rect x="2" y="5" width="36" height="35" rx="4" fill="#f5f5f4" />
                  <ellipse cx="20" cy="8" rx="16" ry="5" fill="#92400e" />
                  <path d="M38 15 Q48 20 38 28" stroke="#f5f5f4" strokeWidth="4" fill="none" />
                  {/* Steam */}
                  <path d="M14 0 Q11 -8 14 -12" stroke="#d1d5db" strokeWidth="2" fill="none" opacity="0.6" />
                  <path d="M24 -2 Q27 -10 24 -15" stroke="#d1d5db" strokeWidth="2" fill="none" opacity="0.6" />
                </g>

                {/* Plant */}
                <g transform="translate(30, 200)">
                  <rect x="10" y="50" width="40" height="45" rx="4" fill="#f472b6" />
                  <rect x="15" y="55" width="30" height="6" rx="2" fill="#f9a8d4" opacity="0.5" />
                  <ellipse cx="30" cy="52" rx="18" ry="6" fill="#22c55e" />
                  <path d="M30 52 Q25 30 15 20" stroke="#16a34a" strokeWidth="3" fill="none" />
                  <path d="M30 52 Q35 25 45 15" stroke="#16a34a" strokeWidth="3" fill="none" />
                  <path d="M30 52 Q30 30 30 10" stroke="#16a34a" strokeWidth="3" fill="none" />
                  <ellipse cx="15" cy="18" rx="8" ry="12" fill="#22c55e" transform="rotate(-30 15 18)" />
                  <ellipse cx="45" cy="13" rx="8" ry="12" fill="#22c55e" transform="rotate(30 45 13)" />
                  <ellipse cx="30" cy="8" rx="7" ry="11" fill="#22c55e" />
                </g>

                {/* Floating notification cards */}
                <g transform="translate(40, 80)">
                  <rect x="0" y="0" width="85" height="50" rx="8" fill="white" filter="drop-shadow(0 2px 4px rgba(0,0,0,0.1))" />
                  <circle cx="22" cy="25" r="14" fill="#dcfce7" />
                  <path d="M17 25 L21 29 L29 21" stroke="#22c55e" strokeWidth="2.5" fill="none" strokeLinecap="round" />
                  <rect x="42" y="16" width="35" height="6" rx="3" fill="#e2e8f0" />
                  <rect x="42" y="28" width="28" height="5" rx="2" fill="#f1f5f9" />
                </g>

                <g transform="translate(300, 80)">
                  <rect x="0" y="0" width="80" height="45" rx="8" fill="white" filter="drop-shadow(0 2px 4px rgba(0,0,0,0.1))" />
                  <circle cx="20" cy="22" r="12" fill="#fef3c7" />
                  <text x="16" y="27" fontSize="12" fill="#f59e0b">⭐</text>
                  <rect x="38" y="14" width="35" height="5" rx="2" fill="#e2e8f0" />
                  <rect x="38" y="24" width="28" height="4" rx="2" fill="#f1f5f9" />
                </g>

                {/* Hearts */}
                <g fill="#f472b6">
                  <path d="M160 100 C160 92 168 88 172 96 C176 88 184 92 184 100 C184 112 172 120 172 120 C172 120 160 112 160 100" />
                  <path d="M340 200 C340 194 346 191 349 197 C352 191 358 194 358 200 C358 209 349 215 349 215 C349 215 340 209 340 200" transform="scale(0.7)" />
                </g>

                {/* Sparkles */}
                <g fill="#fbbf24">
                  <polygon points="150,60 152,66 158,66 154,70 155,76 150,72 145,76 146,70 142,66 148,66" transform="scale(0.7)" />
                  <polygon points="370,150 371,154 375,154 372,157 373,161 370,158 367,161 368,157 365,154 369,154" transform="scale(0.6)" />
                </g>
              </svg>
            </div>
            <div className="order-1 md:order-2">
              <span className="inline-block px-4 py-1 rounded-full bg-accent-100 text-accent-700 text-sm font-medium mb-4">
                For Parents
              </span>
              <h2 className="text-3xl font-bold text-gray-900 mb-6">
                Stay connected, feel at ease
              </h2>
              <ul className="space-y-4">
                <li className="flex items-start gap-3">
                  <svg className="w-6 h-6 text-accent-600 flex-shrink-0 mt-0.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                  </svg>
                  <span className="text-gray-600">Real-time updates throughout the day</span>
                </li>
                <li className="flex items-start gap-3">
                  <svg className="w-6 h-6 text-accent-600 flex-shrink-0 mt-0.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                  </svg>
                  <span className="text-gray-600">Photos and memories to treasure</span>
                </li>
                <li className="flex items-start gap-3">
                  <svg className="w-6 h-6 text-accent-600 flex-shrink-0 mt-0.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                  </svg>
                  <span className="text-gray-600">Instant notifications on your phone</span>
                </li>
                <li className="flex items-start gap-3">
                  <svg className="w-6 h-6 text-accent-600 flex-shrink-0 mt-0.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                  </svg>
                  <span className="text-gray-600">Peace of mind while you're at work</span>
                </li>
                <li className="flex items-start gap-3">
                  <svg className="w-6 h-6 text-accent-600 flex-shrink-0 mt-0.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                  </svg>
                  <span className="text-gray-600">Message your childminder directly in the app</span>
                </li>
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-gradient-to-br from-primary-600 to-primary-700">
        <div className="mx-auto max-w-6xl px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl font-bold text-white sm:text-4xl">
            Ready to try Brightseed?
          </h2>
          <p className="mt-4 text-lg text-primary-100 max-w-2xl mx-auto">
            Sign up in seconds and start sharing updates today.
          </p>
          <div className="mt-10">
            <Link
              to="/register"
              className="inline-block rounded-full bg-white px-8 py-4 text-lg font-semibold text-primary-600 shadow-lg hover:bg-gray-50 transition-colors"
            >
              Get Started Free
            </Link>
          </div>
        </div>
      </section>
    </div>
  )
}
